<script lang="ts">
  import { onMount } from "svelte";
  import { type Writable, writable } from "svelte/store";
  import {
    type Debtor,
    type DebtorInvoiceSub,
    SitesSortFields,
    SortOrder,
  } from "../../../../apis/parkito";
  import { withApi } from "../../../lib/api-parkito";
  import { setCrumbs } from "../../../lib/breadcrumbs";
  import { formatDate } from "../../../utils/dateTime";
  import {
    formatNumber,
    openInvoicePdf,
    translateInvoiceState,
  } from "../../../utils/misc";
  import { type Table, TableComp } from "$components";
  import { Toast } from "$components";
  import LoadingIndicator from "../../LoadingIndicator.svelte";
  import ErrorCloud from "../../ErrorCloud.svelte";

  export let debtor: Debtor;
  let debtorInvoices: DebtorInvoiceSub[] = [];
  let pdfIsLoading = false;
  let isLoading = true;

  let tableData: Writable<Table.Data<5>> = writable({
    headers: [
      { title: "Fakt.nr", type: "text" },
      { title: "Att betala", type: "text" },
      { title: "Fakturadatum", type: "text" },
      { title: "Förfallodatum", type: "text" },
      { title: "Status", type: "text" },
    ],
    rows: [],
  });

  const getDebtorInvoices = async (debtorId: string) => {
    isLoading = true;
    const pageNumber = 1;
    const pageSize = 20;
    const res = await withApi((api) => {
      return api.debtorGetInvoices(debtorId, pageNumber, pageSize);
    });
    isLoading = false;
    if (res.isOk()) {
      debtorInvoices = res.value;
      $tableData.rows = debtorInvoices.map((invoice) => {
        return {
          id: invoice.id.toString(),
          col: [
            invoice.invoiceNr,
            formatNumber(invoice.amount),
            formatDate(invoice.invoiceDate, "YYYY-MM-DD"),
            formatDate(invoice.dueDate, "YYYY-MM-DD"),
            translateInvoiceState(invoice.state),
          ],
        };
      });
    } else {
      Toast.error("Kunde ej hämta fakturor");
    }
  };

  let tableSort: Table.Sort = {
    by: SitesSortFields.CODE,
    order: SortOrder.ASC,
  };

  const fetchInvoicePdf = async (i: number) => {
    if (import.meta.env.DEV) console.log("Fetch invoice pdf clicked.");
    pdfIsLoading = true;
    openInvoicePdf(debtor.id, debtorInvoices[i].id)
      .then((res) => {
        pdfIsLoading = false;
        window.open(res, "_blank");
      })
      .catch((err) => {
        pdfIsLoading = false;
        Toast.error("Kunde inte hämta PDF");
      });
  };

  onMount(() => {
    if (import.meta.env.DEV) console.log("Invoices mounted.");
  });

  $: {
    if (debtor) {
      getDebtorInvoices(debtor?.id ?? "");

      setCrumbs([
        ["Parkörer", "#/debtors"],
        [debtor?.name, `#/debtors/${debtor?.id}`],
        ["Fakturor", `#/debtors/${debtor?.id}/invoices`],
      ]);
    }
  }
</script>

<div>
  {#if debtorInvoices.length > 0}
    <!-- Table -->
    <div class="max-mobile:mx-[-8px]">
      <TableComp
        bind:tableData={$tableData}
        bind:sort={tableSort}
        onClick={fetchInvoicePdf}
        let:prop={row}
      >
        <td>{row.col[0]}</td>
        <td>{row.col[1]}</td>
        <td>{row.col[2]}</td>
        <td>{row.col[3]}</td>
        <td>{row.col[4]}</td>
      </TableComp>
    </div>
  {:else if isLoading}
    <LoadingIndicator visible={true} customClass="py-[96px]" />
  {:else}
    <ErrorCloud
      altMessage="invoice-not-available"
      title="Fakturor saknas"
      text="Det finns inga fakturor att visa."
    />
  {/if}
</div>
